import * as React from 'react';
import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router';
import { useAuth } from '../context/AuthContext';
import useAuthenticatedBootstrap from '../hook/useAuthenticatedBootstrap';
import { useSelector } from 'react-redux';
import MainViewClient from '../routes/MainViewClient';

import DashboardIcon from '@mui/icons-material/Dashboard';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import AppsIcon from '@mui/icons-material/Apps';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DescriptionIcon from '@mui/icons-material/Description';
import ComputerIcon from '@mui/icons-material/Computer';
import ColorModeToggle from '../components/ColorModeToggle';

const drawerWidth = 270;
const collapsedDrawerWidth = 64;

function shouldDisplayItem(item) {
  if (item.service === undefined) return true;
  return Array.isArray(item.service) ? item.service.length > 0 : !!item.service;
}

export default function ClientLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);
  const [openMenus, setOpenMenus] = React.useState({ overview: true, services: false, contacts: false });
  const { data: organizations = [] } = useSelector(
    (state) => state.organizations.organizations
  );

  useAuthenticatedBootstrap();

  const menuSections = React.useMemo(() => ([
    {
      key: 'overview',
      label: 'Vue client',
      icon: <DashboardIcon />,
      items: [
        { label: 'Tableau de bord', path: '/app/home', icon: <DashboardIcon /> },
        { label: 'Analyse temps', path: '/app/time-analytics', icon: <QueryStatsIcon /> },
        { label: 'Vos contrats', path: '/app/contracts', icon: <DescriptionIcon /> },
      ],
    },
    {
      key: 'services',
      label: 'Services',
      icon: <AppsIcon />,
      items: [
        { label: 'Vos licences', path: '/app/o365/licenses', icon: <MailOutlineIcon />, service: true },
        { label: 'Vos utilisateurs', path: '/app/o365/users', icon: <PeopleAltIcon />, service: organizations[0]?.o365Licenses },
        { label: 'Vos appareils', path: '/app/devices', icon: <ComputerIcon />, service: true },
      ],
    },
    {
      key: 'contacts',
      label: 'Contacts',
      icon: <SupportAgentIcon />,
      items: [
        { label: 'Personnel', path: '/app/staff', icon: <PeopleAltIcon /> },
      ],
    },
  ]), [organizations]);

  React.useEffect(() => {
    setOpenMenus((current) => {
      const next = { ...current };
      menuSections.forEach((section) => {
        if (section.items.some((item) => location.pathname.startsWith(item.path))) {
          next[section.key] = true;
        }
      });
      return next;
    });
  }, [location.pathname, menuSections]);

  const toggleMenu = (key) => {
    setOpenMenus((current) => ({ ...current, [key]: !current[key] }));
  };

  const handleSectionClick = (section) => {
    const visibleItems = section.items.filter(shouldDisplayItem);
    if (collapsed) {
      if (visibleItems[0]) navigate(visibleItems[0].path);
      return;
    }
    toggleMenu(section.key);
  };

  const width = collapsed ? collapsedDrawerWidth : drawerWidth;
  const displayName = [user?.firstname, user?.lastname].filter(Boolean).join(' ').trim() || user?.email || 'Client';
  const initials = [user?.firstname?.[0], user?.lastname?.[0]].filter(Boolean).join('').toUpperCase();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Drawer
        variant="permanent"
        sx={{
          width,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: 'width 180ms ease',
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Stack spacing={collapsed ? 0.75 : 1} alignItems="center" sx={{ p: collapsed ? 0.75 : 1.5 }}>
          <Box
            component="img"
            sx={{
              maxWidth: collapsed ? 42 : '70%',
              maxHeight: collapsed ? 30 : 64,
              objectFit: 'contain',
            }}
            alt="Omnibox Logo"
            src="/img/omnibox.png"
          />
          {!collapsed && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Espace client</Typography>
              <Typography variant="caption" color="text.secondary">Portail services</Typography>
            </Box>
          )}
          <Tooltip title={collapsed ? 'Déplier le menu' : 'Réduire le menu'} placement="right" arrow>
            <IconButton
              size="small"
              onClick={() => setCollapsed((value) => !value)}
              sx={{
                width: collapsed ? 34 : 30,
                height: collapsed ? 34 : 30,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Stack>

        {!collapsed && <Divider />}

        <Box sx={{ flex: 1, overflow: 'auto', px: 1, py: 1 }}>
          <List dense>
            {menuSections.map((section) => {
              const visibleItems = section.items.filter(shouldDisplayItem);
              if (!visibleItems.length) return null;
              const isSectionActive = visibleItems.some((item) => location.pathname.startsWith(item.path));
              const isOpen = !!openMenus[section.key];

              return (
                <Box key={section.key}>
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    <Tooltip title={collapsed ? section.label : ''} placement="right" arrow disableHoverListener={!collapsed}>
                      <ListItemButton
                        onClick={() => handleSectionClick(section)}
                        selected={collapsed && isSectionActive}
                        sx={{
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          px: collapsed ? 1 : 2,
                          borderRadius: 1.5,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center', color: isSectionActive ? 'primary.main' : 'inherit' }}>
                          {section.icon}
                        </ListItemIcon>
                        {!collapsed && <ListItemText primary={section.label} primaryTypographyProps={{ fontWeight: isSectionActive ? 800 : 600 }} />}
                        {!collapsed && (isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>

                  {!collapsed && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding dense>
                        {visibleItems.map((item) => {
                          const selected = location.pathname.startsWith(item.path);
                          return (
                            <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
                              <ListItemButton
                                selected={selected}
                                onClick={() => navigate(item.path)}
                                sx={{ pl: 4, borderRadius: 1.5 }}
                              >
                                <ListItemIcon sx={{ minWidth: 34, color: selected ? 'primary.main' : 'inherit' }}>
                                  {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: selected ? 800 : 500 }} />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </Box>
              );
            })}
          </List>
        </Box>

        <Stack
          direction="row"
          sx={{
            p: collapsed ? 1.25 : 2,
            gap: 1,
            justifyContent: collapsed ? 'center' : 'flex-start',
            alignItems: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Avatar sx={{ width: 36, height: 36 }}>{initials}</Avatar>
          {!collapsed && (
            <Box sx={{ mr: 'auto', minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 700, lineHeight: '16px' }}>{displayName}</Typography>
              <Typography variant="caption" noWrap sx={{ display: 'block', color: 'text.secondary' }}>{user?.email}</Typography>
            </Box>
          )}
          {!collapsed && (
            <Tooltip title="Déconnexion" arrow>
              <IconButton size="small" onClick={logout}>
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          px: { xs: 2, md: 4 },
          py: 3,
          overflowY: 'auto',
        }}
      >
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <ColorModeToggle />
        </Stack>
        <MainViewClient />
      </Box>
    </Box>
  );
}
